import {
    type ClipboardEventHandler,
    type FocusEventHandler,
    type KeyboardEventHandler,
    type MouseEventHandler,
    type RefCallback,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';

export interface UseDigitRegisterOptions {
    digits: string;
    onDigitsChange: (digits: string) => void;
    maxDigits: number;
    isDigitAllowed?: (digit: string, position: number, existingDigits: string) => boolean;
}

export interface UseDigitRegisterResult {
    inputProps: {
        ref: RefCallback<HTMLInputElement>;
        onKeyDown: KeyboardEventHandler<HTMLInputElement>;
        onPaste: ClipboardEventHandler<HTMLInputElement>;
        onFocus: FocusEventHandler<HTMLInputElement>;
        onClick: MouseEventHandler<HTMLInputElement>;
        inputMode: 'numeric';
        autoComplete: 'off';
    };
    append: (digit: string) => void;
    removeLast: () => void;
    clear: () => void;
    blur: () => void;
    focus: () => void;
}

const DIGIT = /^[0-9]$/;

export function useDigitRegister({
    digits,
    onDigitsChange,
    maxDigits,
    isDigitAllowed,
}: UseDigitRegisterOptions): UseDigitRegisterResult {
    const inputRef = useRef<HTMLInputElement | null>(null);
    // Mirror digits into a ref so stable callbacks (append/removeLast/clear/...) can read
    // the latest value without taking it as a dependency. Write happens after commit to be
    // concurrent-safe.
    const digitsRef = useRef(digits);
    useEffect(() => {
        digitsRef.current = digits;
    }, [digits]);

    const append = useCallback(
        (digit: string) => {
            if (!DIGIT.test(digit)) {
                return;
            }
            const current = digitsRef.current;
            const next = current === '0' ? (digit === '0' ? '0' : digit) : current + digit;
            if (next.length > maxDigits) {
                return;
            }
            if (isDigitAllowed && !isDigitAllowed(digit, current.length, current)) {
                return;
            }
            if (next === current) {
                return;
            }
            onDigitsChange(next);
        },
        [maxDigits, isDigitAllowed, onDigitsChange]
    );

    const removeLast = useCallback(() => {
        const current = digitsRef.current;
        if (current.length === 0) {
            return;
        }
        onDigitsChange(current.slice(0, -1));
    }, [onDigitsChange]);

    const clear = useCallback(() => {
        if (digitsRef.current.length === 0) {
            return;
        }
        onDigitsChange('');
    }, [onDigitsChange]);

    const moveCursorToEnd = useCallback(() => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        const end = input.value.length;
        input.setSelectionRange(end, end);
    }, []);

    const selectAll = useCallback(() => {
        const input = inputRef.current;
        if (!input || input.value.length === 0) {
            return;
        }
        input.setSelectionRange(0, input.value.length);
    }, []);

    useLayoutEffect(() => {
        if (inputRef.current && document.activeElement === inputRef.current) {
            moveCursorToEnd();
        }
    }, [digits, moveCursorToEnd]);

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (event.ctrlKey || event.metaKey || event.altKey) {
                return;
            }

            const target = event.currentTarget;
            const hasSelection = target.selectionStart !== target.selectionEnd;

            if (event.key === 'Backspace' || event.key === 'Delete') {
                event.preventDefault();
                if (hasSelection) {
                    clear();
                } else {
                    removeLast();
                }
                return;
            }

            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                event.preventDefault();
                moveCursorToEnd();
                return;
            }

            if (event.key.length === 1) {
                event.preventDefault();
                if (!DIGIT.test(event.key)) {
                    return;
                }
                if (hasSelection) {
                    if (!isDigitAllowed || isDigitAllowed(event.key, 0, '')) {
                        onDigitsChange(event.key);
                    }
                } else {
                    append(event.key);
                }
            }
        },
        [append, removeLast, clear, moveCursorToEnd, isDigitAllowed, onDigitsChange]
    );

    const handlePaste: ClipboardEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            event.preventDefault();
            const text = event.clipboardData.getData('text');
            const raw = text.replace(/\D/g, '').slice(0, maxDigits);
            const stripped = raw.replace(/^0+/, '') || (raw.length > 0 ? '0' : '');
            if (stripped !== digitsRef.current) {
                onDigitsChange(stripped);
            }
        },
        [maxDigits, onDigitsChange]
    );

    const focusSelect = useCallback(() => {
        console.log('focusSelect');
        if (digitsRef.current.length > 0) {
            selectAll();
        } else {
            moveCursorToEnd();
        }
    }, [selectAll, moveCursorToEnd]);

    const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(() => {
        // defer the focus until after the browser places its default caret so ours wins
        console.log('handleFocus');
        requestAnimationFrame(focusSelect);
    }, [focusSelect]);

    const handleClick: MouseEventHandler<HTMLInputElement> = useCallback(() => {
        focusSelect();
    }, [focusSelect]);

    const blur = useCallback(() => {
        inputRef.current?.blur();
    }, []);

    const focus = useCallback(() => {
        console.log('focus');
        inputRef.current?.focus();
    }, []);

    const refCallback: RefCallback<HTMLInputElement> = useCallback((node) => {
        inputRef.current = node;
    }, []);

    return {
        inputProps: {
            ref: refCallback,
            onKeyDown: handleKeyDown,
            onPaste: handlePaste,
            onFocus: handleFocus,
            onClick: handleClick,
            inputMode: 'numeric',
            autoComplete: 'off',
        },
        append,
        removeLast,
        clear,
        blur,
        focus,
    };
}
